import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Receipt, CheckCircle2, MapPin, Store } from "lucide-react";
import { formatIDR } from "@/lib/format";
import { useAuth, useCabang } from "@/context/AuthContext";

const CATS = [
  { key: "bahan_baku", label: "Bahan Baku", tone: "bg-blue-100 text-blue-700 border-blue-200", nameField: "item", amountField: "pembelian" },
  { key: "ops", label: "Operasional", tone: "bg-amber-100 text-amber-700 border-amber-200", nameField: "item", amountField: "pembelian" },
  { key: "insentif", label: "Insentif Usaha", tone: "bg-emerald-100 text-emerald-700 border-emerald-200", nameField: "nama", amountField: "realisasi" },
  { key: "lainnya", label: "Lainnya", tone: "bg-slate-100 text-slate-700 border-slate-200", nameField: "item", amountField: "pembelian" },
];

export default function Tagihan() {
  const { user } = useAuth();
  const { cabangList } = useCabang();
  const isSuper = user?.role === "super_admin";
  const isCabangUser = user?.role === "cabang";
  const isViewer = user?.role === "viewer";

  const [admins, setAdmins] = useState([]);
  const [adminId, setAdminId] = useState("");
  const [cabangId, setCabangId] = useState(isCabangUser ? user?.cabang_id : "all");
  const [data, setData] = useState({ items: {}, total: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("semua");

  useEffect(() => {
    if (!isSuper) return;
    api.get("/users")
      .then(({ data }) => setAdmins(data.filter((u) => u.role === "admin"
        && (u.workspace_id === user?.active_workspace_id
          || u.active_workspace_id === user?.active_workspace_id))))
      .catch(() => setAdmins([]));
  }, [isSuper, user?.active_workspace_id]);

  const visibleCabang = useMemo(() => {
    if (!isSuper) return cabangList;
    if (!adminId) return cabangList;
    return cabangList.filter((c) => c.admin_id === adminId);
  }, [cabangList, isSuper, adminId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (cabangId && cabangId !== "all") params.cabang_id = cabangId;
      if (isSuper && adminId) params.admin_id = adminId;
      const { data } = await api.get("/tagihan", { params });
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal memuat tagihan");
    } finally { setLoading(false); }
  }, [cabangId, adminId, isSuper]);

  useEffect(() => { load(); }, [load]);

  const markPaid = async (cat, row) => {
    // Optimistic remove
    const backup = data;
    setData((d) => ({
      ...d,
      items: { ...d.items, [cat]: (d.items[cat] || []).filter((r) => r.id !== row.id) },
      count: Math.max(0, d.count - 1),
      total: d.total - Number(row[CATS.find((c) => c.key === cat).amountField] || 0),
    }));
    try {
      await api.post(`/tagihan/${cat}/${row.id}/pay`);
      toast.success("Tagihan ditandai lunas");
    } catch (err) {
      setData(backup);
      toast.error(err?.response?.data?.detail || "Gagal menandai lunas");
    }
  };

  const nameCabang = (id) => cabangList.find((c) => c.id === id)?.name || "—";

  return (
    <div className="space-y-6">
      <PageHeader
        title={<span className="inline-flex items-center gap-2"><Receipt className="h-6 w-6 text-rose-600" /> Tagihan Belum Lunas</span>}
        subtitle="Daftar item yang belum dibayar. Klik 'Tandai Lunas' saat pembayaran diselesaikan."
      />

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-5 border-rose-200 bg-rose-50/50">
          <div className="text-xs font-semibold uppercase tracking-wide text-rose-700">Total Tagihan</div>
          <div className="mt-1 font-heading text-3xl font-black tracking-tight text-rose-700" data-testid="tagihan-total">
            {formatIDR(data.total || 0)}
          </div>
          <div className="text-xs text-rose-600/80 mt-1">Menunggu pembayaran</div>
        </Card>
        <Card className="p-5 border-slate-200">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Jumlah Item</div>
          <div className="mt-1 font-heading text-3xl font-black tracking-tight text-slate-900" data-testid="tagihan-count">
            {data.count || 0}
          </div>
          <div className="text-xs text-slate-500 mt-1">Baris belum lunas dari semua kategori</div>
        </Card>
      </div>

      {/* Filter */}
      <Card className="p-4 border-slate-200">
        <div className={`grid grid-cols-1 md:grid-cols-2 ${isSuper ? "xl:grid-cols-3" : "xl:grid-cols-2"} gap-3`}>
          {isSuper && (
            <div className="space-y-1">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-1"><Store className="h-3 w-3" /> Usaha</div>
              <Select value={adminId || "all"} onValueChange={(v) => setAdminId(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="tagihan-filter-usaha" className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">Semua Usaha</SelectItem>
                  {admins.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> Cabang</div>
            <Select value={cabangId} onValueChange={setCabangId} disabled={isCabangUser}>
              <SelectTrigger data-testid="tagihan-filter-cabang" className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">Semua Cabang</SelectItem>
                {visibleCabang.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Tabs per kategori */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100 p-1 h-auto flex-wrap">
          <TabsTrigger value="semua" data-testid="tagihan-tab-semua">
            Semua ({data.count || 0})
          </TabsTrigger>
          {CATS.map((c) => (
            <TabsTrigger key={c.key} value={c.key} data-testid={`tagihan-tab-${c.key}`}>
              {c.label} ({data.items?.[c.key]?.length || 0})
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="semua">
          <TagihanTable
            rows={CATS.flatMap((c) => (data.items?.[c.key] || []).map((r) => ({ ...r, _cat: c.key })))}
            catsMap={Object.fromEntries(CATS.map((c) => [c.key, c]))}
            showKategori
            loading={loading}
            isViewer={isViewer}
            nameCabang={nameCabang}
            onMarkPaid={markPaid}
          />
        </TabsContent>

        {CATS.map((c) => (
          <TabsContent key={c.key} value={c.key}>
            <TagihanTable
              rows={(data.items?.[c.key] || []).map((r) => ({ ...r, _cat: c.key }))}
              catsMap={{ [c.key]: c }}
              loading={loading}
              isViewer={isViewer}
              nameCabang={nameCabang}
              onMarkPaid={markPaid}
            />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function TagihanTable({ rows, catsMap, showKategori, loading, isViewer, nameCabang, onMarkPaid }) {
  const sorted = [...rows].sort((a, b) => String(b.tanggal || "").localeCompare(String(a.tanggal || "")));
  return (
    <Card className="p-0 overflow-hidden border-slate-200">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              {showKategori && <TableHead>Kategori</TableHead>}
              <TableHead>Tanggal</TableHead>
              <TableHead>Item / Nama</TableHead>
              <TableHead className="text-right">Nominal</TableHead>
              <TableHead>Cabang</TableHead>
              <TableHead>Catatan</TableHead>
              {!isViewer && <TableHead className="w-40 text-right">Aksi</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody data-testid="tagihan-tbody">
            {loading ? (
              <TableRow>
                <TableCell colSpan={showKategori ? 7 : 6} className="text-center py-8 text-slate-500">Memuat...</TableCell>
              </TableRow>
            ) : sorted.length === 0 ? (
              <TableRow>
                <TableCell colSpan={showKategori ? 7 : 6} className="text-center py-12 text-slate-400">
                  Semua sudah lunas 🎉
                </TableCell>
              </TableRow>
            ) : sorted.map((r) => {
              const cat = catsMap[r._cat];
              return (
                <TableRow key={`${r._cat}-${r.id}`} data-testid={`tagihan-row-${r._cat}-${r.id}`}>
                  {showKategori && (
                    <TableCell>
                      <span className={`inline-flex text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 border ${cat.tone}`}>
                        {cat.label}
                      </span>
                    </TableCell>
                  )}
                  <TableCell className="text-sm">{r.tanggal}</TableCell>
                  <TableCell className="text-sm font-medium">{r[cat.nameField] || "—"}</TableCell>
                  <TableCell className="text-sm text-right font-semibold text-rose-600">
                    {formatIDR(r[cat.amountField])}
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">{nameCabang(r.cabang_id)}</TableCell>
                  <TableCell className="text-sm text-slate-500 max-w-[240px] truncate">{r.catatan || "—"}</TableCell>
                  {!isViewer && (
                    <TableCell className="text-right">
                      <Button size="sm" onClick={() => onMarkPaid(r._cat, r)}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                              data-testid={`pay-${r._cat}-${r.id}`}>
                        <CheckCircle2 className="h-4 w-4 mr-1.5" /> Tandai Lunas
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}
